import { useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RadioGroup,
  RadixSelect,
  SectionCard,
  SegmentedControl,
  StatusBadge,
  StickyFooter,
  toast,
  type SelectOption,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import { useUserGradesQuery } from '@/features/users/grades/hooks/useUserGradesQuery';
import { NOTIFICATION_INTEGRATION_WRITE_ROLES } from '../lib/notificationActionRbac';
import { jobStatusLabel, messageStatusTone } from '../lib/notificationLabels';
import { NotificationTemplateSearchSelect } from '../components/NotificationTemplateSearchSelect';
import { TestSendModal } from '../components/TestSendModal';
import {
  useBulkDispatchMutation,
  type BulkDispatchInput,
  type BulkRecipientInput,
} from '../hooks/useBulkDispatchMutation';
import { useBulkJobQuery } from '../hooks/useBulkJobQuery';
import { useExcelUploadMutation } from '../hooks/usePromotionalMutation';
import type { NotificationChannel } from '../types/notificationMessage';

type SendChannel = 'KAKAO_BIZ' | 'SMS' | 'EMAIL';
type TargetType = 'all' | 'grade' | 'member' | 'manual' | 'excel';
type ScheduleMode = 'immediate' | 'scheduled';

const denseInputStyle = {
  background: 'var(--surface-0)',
  border: '1px solid var(--divider-strong)',
  color: 'var(--content-default)',
} as const;

const parseExternalLines = (text: string): string[] =>
  text
    .split(/[\r\n,;]+/u)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

/** 광고성 메시지 탭 — 채널/템플릿/변수 + 수신자 선택 + 테스트 발송 + 예약 + 발송. (디자인 스펙 §2) */
export const PromotionalTab = () => {
  const { t } = useTranslation('notifications');
  const canWrite = useHasRole(NOTIFICATION_INTEGRATION_WRITE_ROLES);

  const dispatch = useBulkDispatchMutation();
  const excelUpload = useExcelUploadMutation();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [channel, setChannel] = useState<SendChannel>('KAKAO_BIZ');
  const [templateCode, setTemplateCode] = useState<string | null>(null);
  const [smsBody, setSmsBody] = useState('');
  const [varValues, setVarValues] = useState<Record<string, string>>({});

  const [target, setTarget] = useState<TargetType>('all');
  const [gradeId, setGradeId] = useState('');
  const [memberUserIds, setMemberUserIds] = useState<ReadonlyArray<string>>([]);
  const [manualText, setManualText] = useState('');
  const [excelRecipients, setExcelRecipients] = useState<string[]>([]);

  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('immediate');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  const [testOpen, setTestOpen] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);
  const jobQuery = useBulkJobQuery(trackedJobId);

  const gradesQuery = useUserGradesQuery({ page: 1, size: 100 });

  const asNotificationChannel: NotificationChannel = channel;
  // 알림톡·이메일은 템플릿 기반 발송(BE templateCode), SMS 만 본문 직접 입력(rawBody).
  const isTemplateChannel = channel === 'KAKAO_BIZ' || channel === 'EMAIL';

  // 채널 전환 시 다른 채널의 템플릿 코드/치환값이 남지 않도록 초기화.
  const onChannelChange = (next: SendChannel) => {
    if (next === channel) return;
    setChannel(next);
    setTemplateCode(null);
    setVarValues({});
  };

  const gradeOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: '', label: t('promotional.recipients.gradePlaceholder', '발송 대상 등급을 선택하세요') },
      ...((gradesQuery.data?.content ?? []).map((g) => ({
        value: g.gradeId,
        label: `${g.gradeName} (${g.memberCount})`,
      }))),
    ],
    [gradesQuery.data, t],
  );

  const manualCount = parseExternalLines(manualText).length;
  const selectedCount = useMemo(() => {
    switch (target) {
      case 'member':
        return memberUserIds.length;
      case 'manual':
        return manualCount;
      case 'excel':
        return excelRecipients.length;
      default:
        return 0;
    }
  }, [target, memberUserIds.length, manualCount, excelRecipients.length]);

  const onExcelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    excelUpload.mutate(file, {
      onSuccess: (result) => {
        setExcelRecipients(result.recipients);
        const base = t('promotional.recipients.excelUploaded', '{{count}}명 등록됨', { count: result.recipients.length });
        toast.success(
          result.skippedCount > 0
            ? `${base} · ${t('promotional.recipients.excelSkipped', '{{count}}행 건너뜀', { count: result.skippedCount })}`
            : base,
        );
      },
      onError: (err) => toast.error(err.message ?? t('promotional.toast.sendFail', '발송에 실패했습니다.')),
    });
  };

  const buildScheduledAt = (): string | undefined => {
    if (scheduleMode !== 'scheduled' || !scheduleDate || !scheduleTime) return undefined;
    return `${scheduleDate}T${scheduleTime}:00`;
  };

  const buildRecipients = (): BulkRecipientInput[] => {
    if (target === 'member') return memberUserIds.map((userId) => ({ userId }));
    if (target === 'manual') return parseExternalLines(manualText).map((external) => ({ external }));
    if (target === 'excel') return excelRecipients.map((external) => ({ external }));
    return [];
  };

  const hasContent = isTemplateChannel ? Boolean(templateCode) : smsBody.trim().length > 0;
  const hasRecipients =
    target === 'all' || target === 'grade' || selectedCount > 0;
  const canSend = canWrite && hasContent && hasRecipients && !dispatch.isPending;

  const doSend = () => {
    const scheduledAt = buildScheduledAt();
    const payload: BulkDispatchInput = {
      channel: asNotificationChannel,
      ...(isTemplateChannel && templateCode ? { templateCode } : {}),
      ...(channel === 'SMS' && smsBody.trim() ? { rawBody: smsBody } : {}),
      ...(Object.keys(varValues).length > 0 ? { defaultVariables: varValues } : {}),
      ...(scheduledAt ? { scheduledAt } : {}),
    };
    // 수신자 지정
    if (target === 'all') {
      payload.segmentQuery = {};
    } else if (target === 'grade') {
      payload.segmentQuery = gradeId ? { gradeId } : {};
    } else {
      const recipients = buildRecipients();
      if (recipients.length > 0) payload.recipients = recipients;
    }
    dispatch.mutate(payload, {
      onSuccess: (job) => {
        setTrackedJobId(job.jobId);
        setConfirmSend(false);
        toast.success(t('promotional.toast.sendSuccess', '발송이 접수되었습니다. 진행률을 확인하세요.'));
      },
      onError: (err) => {
        setConfirmSend(false);
        toast.error((err as Error).message ?? t('promotional.toast.sendFail', '발송에 실패했습니다.'));
      },
    });
  };

  const onClickSend = () => {
    if (!hasRecipients) {
      toast.error(t('promotional.toast.noRecipients', '수신자를 한 명 이상 선택하세요.'));
      return;
    }
    if (!hasContent) {
      toast.error(t('promotional.toast.noContent', '템플릿을 선택하거나 본문을 입력하세요.'));
      return;
    }
    setConfirmSend(true);
  };

  const testVariables = Object.keys(varValues).length > 0 ? varValues : undefined;

  return (
    <div className="flex flex-col gap-4" data-testid="promotional-tab">
      {/* 섹션 1 — 발송 수단 */}
      <SectionCard title={t('promotional.section.message', '메시지 내용')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('promotional.field.channel', '발송 채널')}
            </span>
            <SegmentedControl<SendChannel>
              value={channel}
              onChange={onChannelChange}
              options={[
                { value: 'KAKAO_BIZ', label: t('channel.KAKAO_BIZ', '알림톡') },
                { value: 'SMS', label: t('channel.SMS', 'SMS') },
                { value: 'EMAIL', label: t('channel.EMAIL', '이메일') },
              ]}
              aria-label={t('promotional.field.channel', '발송 채널')}
            />
          </div>

          {isTemplateChannel ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {channel === 'EMAIL'
                  ? t('promotional.field.emailTemplate', '이메일 템플릿')
                  : t('promotional.field.template', '템플릿')}
              </span>
              <NotificationTemplateSearchSelect
                channel={channel}
                value={templateCode}
                onChange={setTemplateCode}
                placeholder={t('promotional.field.templatePlaceholder', '템플릿을 선택하세요')}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('promotional.field.smsBody', 'SMS 본문')}
              </span>
              <textarea
                rows={5}
                className="w-full rounded-md px-2.5 py-1.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                style={denseInputStyle}
                value={smsBody}
                onChange={(e) => setSmsBody(e.target.value)}
                placeholder={t('promotional.field.smsBodyPlaceholder', 'SMS 로 발송할 본문을 입력하세요.')}
                aria-label={t('promotional.field.smsBody', 'SMS 본문')}
              />
            </div>
          )}

          {/* 변수 치환값(템플릿 선택 시) */}
          {isTemplateChannel && templateCode ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('promotional.field.variables', '변수 치환값')}
              </span>
              <VariableValueEditor values={varValues} onChange={setVarValues} />
              <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {t('promotional.field.variablesHint', '템플릿의 #{변수} 에 들어갈 값을 입력하세요. 비우면 기본값/공란으로 발송됩니다.')}
              </span>
            </div>
          ) : null}
        </div>
      </SectionCard>

      {/* 섹션 2 — 수신자 */}
      <SectionCard
        title={t('promotional.recipients.title', '수신자 선택')}
        description={t('promotional.section.recipients', '수신자')}
      >
        <div className="flex flex-col gap-4">
          <RadioGroup
            value={target}
            onValueChange={(v) => setTarget(v as TargetType)}
            orientation="vertical"
            options={[
              { value: 'all', label: t('common.all', '전체') },
              { value: 'grade', label: t('promotional.recipients.mode.grade', '회원 등급') },
              { value: 'member', label: t('promotional.recipients.mode.member', '회원 목록') },
              { value: 'manual', label: t('promotional.recipients.mode.manual', '직접 입력') },
              { value: 'excel', label: t('promotional.recipients.mode.excel', '엑셀 업로드') },
            ]}
            aria-label={t('promotional.recipients.title', '수신자 선택')}
          />

          {target === 'grade' ? (
            <div className="max-w-[320px]">
              <RadixSelect
                value={gradeId}
                onChange={setGradeId}
                options={gradeOptions}
                aria-label={t('promotional.recipients.mode.grade', '회원 등급')}
              />
            </div>
          ) : null}

          {target === 'member' ? (
            <UserSearchSelect
              multi
              value={memberUserIds}
              onChange={setMemberUserIds}
              testId="promotional-members"
              ariaLabel={t('promotional.recipients.mode.member', '회원 목록')}
            />
          ) : null}

          {target === 'manual' ? (
            <textarea
              rows={4}
              className="w-full rounded-md px-2.5 py-1.5 font-mono text-[12px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
              style={denseInputStyle}
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              placeholder={t('promotional.recipients.manualPlaceholder', '이메일·전화번호를 줄바꿈 또는 쉼표로 구분해 입력하세요.')}
              aria-label={t('promotional.recipients.mode.manual', '직접 입력')}
            />
          ) : null}

          {target === 'excel' ? (
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={onExcelChange}
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={excelUpload.isPending}
                  onClick={() => fileRef.current?.click()}
                >
                  {excelUpload.isPending
                    ? t('promotional.recipients.excelUploading', '업로드 중…')
                    : t('promotional.recipients.excelChoose', '엑셀 파일 선택')}
                </Button>
                {excelRecipients.length > 0 ? (
                  <Badge variant="neutral" size="sm">
                    {t('promotional.recipients.excelUploaded', '{{count}}명 등록됨', { count: excelRecipients.length })}
                  </Badge>
                ) : null}
              </div>
              <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {t('promotional.recipients.excelHint', '엑셀(.xlsx) 파일을 업로드하면 수신자 목록을 일괄 등록합니다.')}
              </span>
            </div>
          ) : null}

          {selectedCount > 0 ? (
            <p className="text-[12px]" style={{ color: 'var(--content-default)' }}>
              {t('promotional.recipients.selectedCount', '선택된 수신자 {{count}}명', { count: selectedCount })}
            </p>
          ) : null}
        </div>
      </SectionCard>

      {/* 섹션 3 — 발송 설정 */}
      <SectionCard title={t('promotional.section.schedule', '발송 시점')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('promotional.schedule.mode', '발송 시점')}
            </span>
            <SegmentedControl<ScheduleMode>
              value={scheduleMode}
              onChange={setScheduleMode}
              options={[
                { value: 'immediate', label: t('promotional.schedule.immediate', '즉시 발송') },
                { value: 'scheduled', label: t('promotional.schedule.scheduled', '예약 발송') },
              ]}
              aria-label={t('promotional.schedule.mode', '발송 시점')}
            />
          </div>
          {scheduleMode === 'scheduled' ? (
            <div className="flex flex-wrap items-center gap-3">
              <Input
                type="date"
                size="md"
                className="max-w-[180px]"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                aria-label={t('promotional.schedule.at', '예약 일시')}
              />
              <Input
                type="time"
                size="md"
                className="max-w-[140px]"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                aria-label={t('promotional.schedule.at', '예약 일시')}
              />
            </div>
          ) : null}
          <p
            className="rounded-md p-3 text-[12px]"
            style={{ background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' }}
            role="note"
          >
            {t('promotional.advertising.notice', '광고성 메시지는 (광고) 표기·야간 발송 제한·수신거부 안내가 자동 적용됩니다.')}
          </p>
        </div>
      </SectionCard>

      {/* 진행률 */}
      {trackedJobId && jobQuery.data ? (
        <SectionCard title={t('promotional.section.progress', '발송 진행률')}>
          <dl className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-3">
            <div>
              <dt style={{ color: 'var(--content-muted)' }}>{t('promotional.progress.status', '상태')}</dt>
              <dd className="mt-1">
                <StatusBadge label={jobStatusLabel(t, jobQuery.data.status)} variant={messageStatusTone(jobQuery.data.status)} />
              </dd>
            </div>
            <div>
              <dt style={{ color: 'var(--content-muted)' }}>{t('promotional.progress.success', '성공')}</dt>
              <dd className="mt-1 tabular-nums" style={{ color: 'var(--content-strong)' }}>
                {jobQuery.data.successCount} / {jobQuery.data.totalCount}
              </dd>
            </div>
            <div>
              <dt style={{ color: 'var(--content-muted)' }}>{t('promotional.progress.fail', '실패')}</dt>
              <dd className="mt-1 tabular-nums" style={{ color: 'var(--signal-danger)' }}>
                {jobQuery.data.failCount}
              </dd>
            </div>
          </dl>
        </SectionCard>
      ) : null}

      <StickyFooter>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasContent || dispatch.isPending}
          onClick={() => setTestOpen(true)}
        >
          {t('promotional.actions.test', '테스트 발송')}
        </Button>
        <Button size="sm" disabled={!canSend} onClick={onClickSend}>
          {dispatch.isPending
            ? t('promotional.actions.sending', '발송 중…')
            : t('promotional.actions.send', '발송')}
        </Button>
      </StickyFooter>

      <TestSendModal
        open={testOpen}
        onOpenChange={setTestOpen}
        channel={asNotificationChannel}
        templateCode={templateCode}
        rawBody={channel === 'SMS' ? smsBody : undefined}
        variables={testVariables}
      />

      {/* 발송 확인 */}
      <Dialog open={confirmSend} onOpenChange={setConfirmSend}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('promotional.actions.send', '발송')}</DialogTitle>
            <DialogDescription>
              {t('promotional.advertising.notice', '광고성 메시지는 (광고) 표기·야간 발송 제한·수신거부 안내가 자동 적용됩니다.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('common.cancel', '취소')}
              </Button>
            </DialogClose>
            <Button size="sm" disabled={dispatch.isPending} onClick={doSend}>
              {dispatch.isPending ? t('promotional.actions.sending', '발송 중…') : t('promotional.actions.send', '발송')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface VariableValueEditorProps {
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

/** 변수 치환값 입력 — 임의 키/값 추가(템플릿 변수 카탈로그는 be-notif 가 노출하면 후속 연동). */
const VariableValueEditor = ({ values, onChange }: VariableValueEditorProps) => {
  const { t } = useTranslation('notifications');
  const [newKey, setNewKey] = useState('');
  const entries = Object.entries(values);

  const addKey = () => {
    const key = newKey.trim();
    if (!key || key in values) return;
    onChange({ ...values, [key]: '' });
    setNewKey('');
  };

  return (
    <div className="flex flex-col gap-2">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center gap-2">
          <span
            className="inline-flex h-8 w-[40%] min-w-[120px] items-center rounded-md px-2 font-mono text-[12px]"
            style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }}
          >
            {`#{${key}}`}
          </span>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange({ ...values, [key]: e.target.value })}
            className="h-8 flex-1 rounded-md px-2.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
            style={{ ...denseInputStyle, color: 'var(--content-strong)' }}
            aria-label={`${key} 치환값`}
          />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value.replace(/[^A-Za-z0-9_]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newKey.trim()) {
              e.preventDefault();
              addKey();
            }
          }}
          placeholder={t('promotional.field.variables', '변수 치환값')}
          className="h-8 max-w-[200px] rounded-md px-2.5 font-mono text-[12px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
          style={denseInputStyle}
          aria-label={t('promotional.field.variables', '변수 치환값')}
        />
        <Button variant="outline" size="sm" disabled={!newKey.trim()} onClick={addKey}>
          {t('common.add', '추가')}
        </Button>
      </div>
    </div>
  );
};
